import { PreprocessorUtils, dataflowLexerWoAnno } from './dataflow-preprocessor-utils';

const antlr4 = require('antlr4');

// The model of tokens in our basic preprocessor
export interface IProcessedToken {
    // head could be of regular annotations string or a CommentToken where we could put WS to its tail
    head: Array<string | IHeadComment>,		// The area of annotations, head: ['Anno1.', 'Anno2.'] will be formatted as /* Anno1 Anno2 */
    text: string,		// The text of this token
    tail: string[],		// The reserved area(currently for the following WS)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type: any			// The type of this token
}

interface IHeadComment {
    type: 'COMMENT',
    text: string,
    tail: string[],
    // should never be used
    head: string[]
}

/**
 * PreprocessorsBase provide basic serialization and rule services
 */
export class PreprocessorsBase {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _lexer: any;
    protected tokenList: IProcessedToken[];
    protected currentToken: IProcessedToken;
    private _result: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private ruleMap: Map<any, Array<() => void>>;	// The map of (current token's type) -> (functions to be called)

    // To format annotations from ['Anno1', 'Anno2'] to /* Anno1 Anno2 */
    public static serializeAnnotation(annotationArr: Array<string | IHeadComment>): string {
        if (annotationArr.length === 0) {
            return '';
        }

        const headComments: IHeadComment[] = annotationArr.filter((one) => typeof one === 'object' && one.type === 'COMMENT') as IHeadComment[];
        const headAnnotations: string[] = annotationArr.filter((one) => typeof one === 'string') as string[];

        return PreprocessorUtils.annotationName.annotationHead +
            Array.from(new Set(headAnnotations)).join(' ') +
            headComments.map((oneHeadComment) => oneHeadComment.text + oneHeadComment.tail.join('')).join('') +
            PreprocessorUtils.annotationName.annotationTail;
    }

    /***
     * Get the previous token preprocessed
     * @param fromHeads allow to take token from the current head
     * @protected
     */
    protected getLastToken(fromHeads: boolean = false): IProcessedToken | undefined {
        let nullableLastHeadComment: IHeadComment | undefined;
        if (fromHeads && this.currentToken.head.length) {
            const currentReversedHeads = this.currentToken.head.slice().reverse();
            nullableLastHeadComment = currentReversedHeads.find((one) => typeof one === 'object' && one.type === 'COMMENT') as IHeadComment;
        }

        if (fromHeads && nullableLastHeadComment){
            return nullableLastHeadComment;
        }

        if (this.tokenList.length < 1) {
            return undefined;
        }

        return this.tokenList[this.tokenList.length - 1];
    }

    constructor(script: string) {
        this._result = PreprocessorUtils.isPreprocessed(script) ? script : '';
        this._lexer = new dataflowLexerWoAnno(new antlr4.InputStream(script));
        this.tokenList = [{head: [], text: '', tail: [], type: dataflowLexerWoAnno.WS}];
        this.currentToken = {head: [], text: '', tail: [], type: null};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.ruleMap = new Map<any, Array<() => void>>();
        this.enroll();	// Add the preprocessor's rules
    }

    public get result(): string {
        if (this._result === '') {
            this._lexer.reset();
            this.tokenList = [{head: [], text: '', tail: [], type: dataflowLexerWoAnno.WS}];
            // Traverse the script with the origin antlr4 lexer
            let token = this._lexer.nextToken();
            while (token && token.type !== dataflowLexerWoAnno.EOF) {
                this.visit(token);	// Some actions here in this.visit()
                token = this._lexer.nextToken();
            }
            // The preprocessed strings should start with /* Annotation */
            if (this.tokenList.length > 0) {
                this.tokenList[0].head.unshift(PreprocessorUtils.annotationName.enableAnnotation);
            }
            // The 'tail' field should not be formatted.
            this._result = this.tokenList.map((token: IProcessedToken) => (PreprocessorsBase.serializeAnnotation(token.head) + token.text + token.tail.join(''))).join('');
        }
        return this._result;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected enrollOneProcessor(tokenType: any, rule: () => void): void {
        if (!this.ruleMap.has(tokenType)) {
            this.ruleMap.set(tokenType, []);
        }
        this.ruleMap.get(tokenType).push(rule);
    }

    protected enroll(): void {
        // by default enroll the processor to handle comments
        this.enrollOneProcessor(dataflowLexerWoAnno.COMMENT, () => {
            // We treat comments as a special type of annotations.
            // For example, /*dasdas*/das with annotation "A" "B" "C" is preprocessed to /* /*dasdas*/ A B C */das
            this.currentToken.head.push({
                type: 'COMMENT',
                tail: [],
                text: this.currentToken.text,
                head: []
            });
        });

        // by default enroll the processor to handle white spaces
        this.enrollOneProcessor(dataflowLexerWoAnno.WS, () => {
            // If we got last token
            const currentLastToken = this.getLastToken(true);
            if (currentLastToken) {
                // When scanning WS tokens, add their text to the previous token
                currentLastToken.tail.push(this.currentToken.text);
            } else {
                // Put it back to the head if we are the first token and there were no tail token
                this.currentToken.head.push(this.currentToken.text);
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private visit(token: any): void {
        // Retain the previous head and tail
        this.currentToken.text = token.text;
        this.currentToken.type = token.type;
        // Execute the registered rules
        if (this.ruleMap.has(token.type)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.ruleMap.get(token.type).forEach((rule: (token: any) => void) => {
                rule(token);
            });
        }
        // Push the current token into tokenList
        // only for DEFAULT_CHANNEL channel, and COMMENT shouldn't be an independent token
        if (token.channel === 0 && token.type !== dataflowLexerWoAnno.COMMENT) {
            this.tokenList.push(this.currentToken);
            // Emplace a new token
            this.currentToken = {head: [], text: '', tail: [], type: null};
        }
    }
}
